import { createForm } from "@tanstack/solid-form";

import { createMemo, Show } from "solid-js";
import { produce } from "solid-js/store";
import { toast } from "solid-sonner";
import { z } from "zod";
import { useI18n } from "@/api/i18n";
import { DialogImageUploader } from "@/components/shared/dialog-image-uploader";
import { LocationPopoverForm } from "@/components/shared/geo-code/location-popover";
import {
  FileInput,
  PhoneInput,
  phoneNumberSchema,
} from "@/components/shared/input";
import { DatePicker } from "@/components/ui/date-picker";
import {
  FieldContainer,
  FieldContainerLabel,
  FieldInfo,
  FieldSectionTitle,
  Input,
} from "@/components/ui/input";
import { UPLOAD_RULES } from "@/domains/documents/constants/upload";
import { getUploadApiError } from "@/domains/documents/utils/upload-error";
import { useUpdateProfileImage } from "@/domains/freelance/profile/api";
import { parseDateOrNull } from "@/libs/date";
import profile_picture from "../../assets/profile-pic.png";
import { useOnboardingStore } from "../../context/OnboardingContext";
import {
  defaultBirthdateView,
  isAdultBirthdate,
  MINOR_BIRTHDATE_MESSAGE,
  maxAdultBirthdate,
} from "../../utils/birthdate";
import { OnboardingDescription, OnboardingTitle } from "../shared/text";

const validationSchema = z.object({
  date_of_birth: z
    .date({ message: "Date of birth is required" })
    .refine((date) => isAdultBirthdate(date), {
      message: MINOR_BIRTHDATE_MESSAGE,
    }),
  residency: z
    .string({ message: "Residency is required" })
    .min(3, { message: "Residency must be at least 3 characters" })
    .max(255, { message: "Residency must be less than 255 characters" }),
  phone_number: phoneNumberSchema.optional(),
  resume: z.instanceof(File).optional(),
  email: z.string().email({ message: "Email non valida" }).optional(),
});

export const Info = () => {
  const [store, setStore] = useOnboardingStore();
  const { t } = useI18n();

  // Stable 18+ boundary so the date picker uses one maxDate for the whole calendar render
  const maxBirthdate = createMemo(() => maxAdultBirthdate());
  const birthdateView = createMemo(() => defaultBirthdateView());

  // Verifica se l'email è temporanea
  const isTemporaryEmail = () =>
    store.profile.email?.includes("@sevedemo.invite") ?? false;

  const form = createForm(() => ({
    defaultValues: {
      date_of_birth: parseDateOrNull(store.info.value.date_of_birth),
      residency: store.info.value.residency,
      phone_number: store.info.value.phone_number,
      resume: store.info.value.resume,
      email: isTemporaryEmail() ? "" : undefined,
    },
    schema: validationSchema,
    onSubmit: async ({ value }) => {
      const validatedData = validationSchema.safeParse(value);
      if (!validatedData.success) {
        console.error("Validation error", validatedData.error);
        return;
      }

      // Update store with validated data
      setStore(
        produce((draft) => {
          draft.info.value.date_of_birth = validatedData.data.date_of_birth;
          draft.info.value.residency = validatedData.data.residency;
          draft.info.value.phone_number = validatedData.data.phone_number;
          draft.info.value.resume = validatedData.data.resume;
          // Se l'email è stata fornita e l'email attuale è temporanea, aggiorna il profilo
          if (validatedData.data.email && isTemporaryEmail()) {
            draft.profile.email = validatedData.data.email;
          }
          draft.info.error = null;
        }),
      );
    },
  }));

  const updateProfileImage = useUpdateProfileImage();

  const handleUpload = async (image: File) => {
    toast.loading(t("common.image_upload.loading"), { id: "onboarding-image" });
    try {
      const response = await updateProfileImage.mutateAsync({ image });
      toast.success(t("common.image_upload.success"), {
        id: "onboarding-image",
      });
      setStore(
        produce((draft) => {
          draft.info.value.avatar_url = response.profile.avatar_url;
        }),
      );
    } catch (error) {
      // Upload errors (413/415) are shown inline by the dialog (D10); dismiss the
      // loading toast and rethrow so the dialog can map them. Others keep the toast.
      if (getUploadApiError(error)) {
        toast.dismiss("onboarding-image");
      } else {
        toast.error(t("common.image_upload.error"), { id: "onboarding-image" });
      }
      throw error;
    }
  };

  const handleFileChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (file) {
      form.setFieldValue("resume", file);
      setStore(
        produce((draft) => {
          draft.info.value.resume = file;
        }),
      );
    } else {
      form.setFieldValue("resume", undefined);
      setStore(
        produce((draft) => {
          draft.info.value.resume = undefined;
        }),
      );
    }
  };

  const handleRemoveResume = () => {
    form.setFieldValue("resume", undefined);
    setStore(
      produce((draft) => {
        draft.info.value.resume = undefined;
      }),
    );
  };

  return (
    <div class="flex flex-col gap-8 w-full">
      <div class="flex flex-col gap-4">
        <OnboardingTitle>{t("onboarding.steps.info.title")}</OnboardingTitle>
        <OnboardingDescription>
          {t("onboarding.steps.info.description")}
        </OnboardingDescription>
      </div>
      <div class="flex flex-col md:flex-row gap-4 justify-between items-start">
        <div class="w-1/4">
          <DialogImageUploader
            onUpload={handleUpload}
            uploadRules={UPLOAD_RULES.profileImage}
          >
            <div
              class="w-24 h-24 md:w-48 md:h-48 rounded-full flex items-center justify-center relative hover:opacity-80 transition-opacity duration-300 overflow-hidden"
              classList={{
                "bg-gray-200": !store.info.value.avatar_url,
              }}
            >
              <Show
                when={store.info.value.avatar_url}
                fallback={<img src={profile_picture} alt="User Profile" />}
              >
                <img src={store.info.value.avatar_url!} alt="User Profile" />
              </Show>
            </div>
            <Show
              when={!store.info.value.avatar_url}
              fallback={
                <span class="text-sm">{t("common.image_upload.edit")}</span>
              }
            >
              <span class="text-sm">{t("common.image_upload.upload")}</span>
            </Show>
          </DialogImageUploader>
        </div>
        <div class="flex flex-col gap-8 w-full md:w-3/4">
          <form class="space-y-4">
            <div class="border-b border-neutral-200 pb-4 w-full space-y-4">
              <FieldSectionTitle class="text-md font-medium">
                Informazioni personali
              </FieldSectionTitle>

              <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <form.Field
                  name="date_of_birth"
                  validators={{
                    onChange: ({ value }) => {
                      const result =
                        validationSchema.shape.date_of_birth.safeParse(value);
                      return result.success
                        ? undefined
                        : result.error.issues[0].message;
                    },
                  }}
                  listeners={{
                    onChange: ({ value }) => {
                      setStore(
                        produce((draft) => {
                          draft.info.value.date_of_birth = value;
                        }),
                      );
                    },
                  }}
                >
                  {(field) => (
                    <FieldContainer class="w-fit">
                      <FieldContainerLabel for="date_of_birth">
                        {t("onboarding.steps.info.dateOfBirth")} *
                      </FieldContainerLabel>
                      <DatePicker
                        id={field().name}
                        value={field().state.value}
                        onChange={(date) => {
                          field().handleChange(date);
                        }}
                        placeholder={t("onboarding.steps.info.dateOfBirthHelp")}
                        maxDate={maxBirthdate()}
                        defaultViewDate={birthdateView()}
                      />
                      <FieldInfo
                        field={field()}
                        helpText={t("onboarding.steps.info.dateOfBirthHelp")}
                      />
                    </FieldContainer>
                  )}
                </form.Field>

                <form.Field
                  name="phone_number"
                  validators={{
                    onChange: ({ value }) => {
                      if (!value) {
                        return undefined;
                      }
                      const result =
                        validationSchema.shape.phone_number.safeParse(value);
                      return result.success
                        ? undefined
                        : result.error.issues[0].message;
                    },
                  }}
                  listeners={{
                    onChange: ({ value }) => {
                      setStore(
                        produce((draft) => {
                          draft.info.value.phone_number = value;
                        }),
                      );
                    },
                  }}
                >
                  {(field) => (
                    <FieldContainer>
                      <FieldContainerLabel for="phone_number">
                        {t("onboarding.steps.info.phoneNumber")}
                        <span class="text-xs text-gray-500 italic ml-1">
                          {" "}
                          (opzionale)
                        </span>
                      </FieldContainerLabel>
                      <PhoneInput fieldApi={field()} />
                    </FieldContainer>
                  )}
                </form.Field>

                <Show when={isTemporaryEmail()}>
                  <form.Field
                    name="email"
                    validators={{
                      onChange: ({ value }) => {
                        if (!value) {
                          return "Email richiesta per completare l'onboarding";
                        }
                        const result =
                          validationSchema.shape.email.safeParse(value);
                        return result.success
                          ? undefined
                          : result.error.issues[0].message;
                      },
                    }}
                  >
                    {(field) => (
                      <FieldContainer>
                        <FieldContainerLabel for="email">
                          Email *
                        </FieldContainerLabel>
                        <Input
                          id={field().name}
                          name={field().name}
                          type="email"
                          value={
                            field().state.value ?? store.profile.email ?? ""
                          }
                          onInput={(e) => field().handleChange(e.target.value)}
                          onBlur={() => field().handleBlur()}
                          placeholder="nome@esempio.com"
                        />
                        <FieldInfo field={field()} />
                        <p class="text-xs text-gray-500 mt-1">
                          Questa email verrà utilizzata per accedere al tuo
                          account
                        </p>
                      </FieldContainer>
                    )}
                  </form.Field>
                </Show>
              </div>
              <form.Field
                name="residency"
                validators={{
                  onChange: ({ value }) => {
                    const result =
                      validationSchema.shape.residency.safeParse(value);
                    return result.success
                      ? undefined
                      : result.error.issues[0].message;
                  },
                }}
                listeners={{
                  onChange: ({ value }) => {
                    setStore(
                      produce((draft) => {
                        draft.info.value.residency = value;
                      }),
                    );
                  },
                }}
              >
                {(field) => (
                  <FieldContainer>
                    <FieldContainerLabel for="residency">
                      {t("onboarding.steps.info.residency")}
                    </FieldContainerLabel>
                    <LocationPopoverForm fieldsApi={field()} />
                    <FieldInfo
                      field={field()}
                      helpText={t("onboarding.steps.info.residencyHelp")}
                    />
                  </FieldContainer>
                )}
              </form.Field>
            </div>

            <div class="w-full space-y-4">
              <form.Field
                name="resume"
                validators={{
                  onChange: validationSchema.shape.resume,
                }}
              >
                {(field) => (
                  <FieldContainer>
                    <FieldContainerLabel for="resume">
                      {t("profile.tabs.professional.edit.skills.resume.title")}
                    </FieldContainerLabel>
                    <FileInput
                      fieldApi={field()}
                      onFileChange={handleFileChange}
                      onRemoveFile={handleRemoveResume}
                      uploadRules={UPLOAD_RULES.resume}
                    />
                    <FieldInfo field={field()} />
                  </FieldContainer>
                )}
              </form.Field>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
